import { BREAKPOINTS } from '@app/constants/breakpoints';
import { useIsScreenSize } from '@app/utils/hooks';
import { useTableColumnsConfiguration } from '@app/utils/hooks/table/useTableColumnsConfiguration';
import { Column, Table } from '@tanstack/react-table';
import { ColumnsConfiguration, TablesIdentifier } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../Button';
import { Dropdown, DropdownMenu, DropdownTrigger } from '../Dropdown';
import { Columns, Visibility } from '../Icons';

interface Props<T> {
  table: Table<T>;
  columnsConfiguration: ColumnsConfiguration;
  tableName: TablesIdentifier;
  onChange?: (columnId: string, isVisible: boolean) => void;
}

export const TableControls = <T,>({ table, columnsConfiguration, tableName, onChange }: Props<T>) => {
  const { t } = useTranslation(['common']);
  const { setColumnsVisibility } = useTableColumnsConfiguration({ tableName });
  const isMobile = useIsScreenSize(BREAKPOINTS.LG);

  const allTableColumns = table.getAllLeafColumns();
  const editableColumns = React.useMemo(() => allTableColumns.filter(column => column.columnDef.enableHiding !== false), [allTableColumns]);

  const visibleColumns = React.useMemo(
    () =>
      editableColumns.filter(column => {
        return columnsConfiguration[column.id]?.isVisible ?? false;
      }),
    [editableColumns, columnsConfiguration],
  );

  const areAllSelected = visibleColumns.length === editableColumns.length;

  const getColumnHeader = React.useCallback((column: Column<T>) => {
    const { header } = column.columnDef;

    if (typeof header === 'string') {
      return header;
    }

    return '';
  }, []);

  const toggleVisibility = (column: Column<T>, value = !column.getIsVisible()) => {
    onChange?.(column.id, value);
    column.toggleVisibility(value);
  };

  const dropdownItems = editableColumns.map(column => ({
    key: column.id,
    label: getColumnHeader(column),
  }));

  const handleSelectionChange = (columnIds: string[]) => {
    setColumnsVisibility(
      editableColumns.reduce((acc, column) => {
        return {
          ...acc,
          [column.id]: { isVisible: columnIds.includes(column.id) },
        };
      }, {}),
    );

    editableColumns.forEach(column => {
      const isVisible = columnIds.includes(column.id);

      if (isVisible !== column.getIsVisible()) {
        toggleVisibility(column, columnIds.includes(column.id));
      }
    });
  };

  return (
    <Dropdown title={isMobile ? t('common:attribute_other') : t('common:column_other')} placement="bottom-start">
      <DropdownTrigger>
        {({ isOpen, triggerProps }) => (
          <Button
            size="medium"
            variant="bordered"
            color="secondary"
            className="pl-3 pr-2 lg:pl-6 lg:pr-5"
            icon={isMobile ? Visibility : Columns}
            isChevronOpen={isOpen}
            isActive={!areAllSelected}
            {...triggerProps}
          >
            <span className="flex items-center gap-1">
              {!isMobile && t('common:column_other')}
              {isMobile && !areAllSelected && `${visibleColumns.length}/${editableColumns.length}`}
            </span>
          </Button>
        )}
      </DropdownTrigger>
      <DropdownMenu
        selectionMode="multiple"
        selectedKeys={visibleColumns.map(column => column.id)}
        items={dropdownItems}
        onSelectionChange={handleSelectionChange}
      />
    </Dropdown>
  );
};
